import { type MatchPlayerDto, itemById, kitById } from '@penalty-cup/shared';
import type { CSSProperties } from 'react';
import { Celebration } from '../components/Celebration.js';
import { Goal } from '../components/Goal.js';
import { Meter } from '../components/Meter.js';
import { Scoreboard } from '../components/Scoreboard.js';
import { secondsLeft, useWallClock } from '../components/useNow.js';
import { myRole } from '../game/session.js';
import { useApp } from '../store.js';
import { OUTCOME_TITLE, QUALITY_LABEL, isFinalRound, roundTitle } from '../strings.js';

const TURN_SECONDS = 10;

function TurnTimer({ deadline, offset }: { deadline: number; offset: number }) {
  const now = useWallClock();
  // До первого кадра считаем, что время только началось
  const left = now ? secondsLeft(deadline, offset, now) : TURN_SECONDS;
  return (
    <div className={`timer ${left < 3 ? 'timer--low' : ''}`} role="timer">
      <span className="timer__bar" style={{ width: `${Math.min(100, (left / TURN_SECONDS) * 100)}%` }} />
      <span className="timer__text">{Math.ceil(left)}</span>
    </div>
  );
}

/** Имя игрока в его рамке из магазина (видна сопернику). */
function Name({ p, you }: { p: MatchPlayerDto; you: boolean }) {
  const frame = itemById(p.frame);
  const framed = frame && !frame.free;
  return (
    <span
      className={`${you ? 'intro__you' : ''} ${framed ? 'framed' : ''}`}
      style={
        framed ? ({ '--frame-1': frame.colors[0], '--frame-2': frame.colors[1] } as CSSProperties) : undefined
      }
    >
      {p.name}
    </span>
  );
}

export function Match() {
  const s = useApp();
  const { match, turn, stage, draft, lastKick, tournament } = s;
  if (!match) return null;
  const role = myRole(s);
  const [a, b] = match.players;
  const tMatch = tournament?.matches.find((m) => m.id === match.matchId);
  const roundName = tMatch && tournament ? roundTitle(tMatch.round, tournament.size) : '';

  // Интро: игроки и раунд турнира, пока сервер ждёт готовности
  if (match.phase === 'READY') {
    // Финал — интро с кубком (у дуэли единственный матч — без пафоса финала)
    const final =
      !!tMatch && !!tournament && tournament.size > 2 && isFinalRound(tMatch.round, tournament.size);
    return (
      <main className={`screen intro ${final ? 'intro--final' : ''}`}>
        {final && (
          <div className="intro__trophy" aria-hidden="true">
            🏆
          </div>
        )}
        <span className="intro__round">{roundName}</span>
        <div className="intro__vs">
          <Name p={a} you={match.you === 'A'} />
          <b>VS</b>
          <Name p={b} you={match.you === 'B'} />
        </div>
        <p className="screen__lead">Серия пенальти: по 5 ударов, дальше — до первой разницы</p>
      </main>
    );
  }

  // Вратарь — сторона, которая не бьёт (в текущем или только что раскрытом ударе)
  const shooterSide = turn?.shooter ?? lastKick?.kick.shooter ?? 'A';
  const keeperColor = kitById(match.players.find((p) => p.side !== shooterSide)?.kit).color;
  const label = turn ? (turn.suddenDeath ? 'Sudden Death' : `Удар ${turn.round} из 5`) : roundName;
  const showResult = !turn && lastKick;
  const youShot = lastKick?.kick.shooter === match.you;
  const goodForYou = lastKick ? (lastKick.kick.outcome === 'GOAL') === youShot : false;
  // Стадион — свой (из магазина), мяч — бьющего, празднование — своего гола
  const stadium = itemById(s.me?.equipped.stadium);
  const stadiumStyle =
    stadium && !stadium.free
      ? ({ '--stadium-1': stadium.colors[0], '--stadium-2': stadium.colors[1] } as CSSProperties)
      : undefined;
  const ballColors = itemById(match.players.find((p) => p.side === shooterSide)?.ball)?.colors;
  const yourGoal = !!showResult && youShot && lastKick.kick.outcome === 'GOAL';

  let prompt = '';
  if (turn && role === 'shooter') {
    prompt = stage === 'zone' ? 'Твой удар — выбери, куда бить' : stage === 'sent' ? 'Ждём вратаря…' : '';
  } else if (turn && role === 'keeper') {
    prompt = stage === 'zone' ? 'Ты в воротах — куда прыгнешь?' : 'Ждём удара…';
  }

  return (
    <main
      className={`screen match ${turn?.suddenDeath ? 'match--sudden' : stadiumStyle ? 'stadium' : ''}`}
      style={stadiumStyle}
    >
      {yourGoal && <Celebration key={lastKick.kick.seq} itemId={s.me?.equipped.celebration} />}
      <Scoreboard match={match} label={label} />
      {turn?.suddenDeath && turn.round === 6 && turn.shooter === 'A' && (
        <div key={turn.roundId} className="sudden" role="status">
          <b>SUDDEN DEATH</b>
          <span>до первой разницы</span>
        </div>
      )}
      <Goal
        selectable={!!turn && stage === 'zone'}
        selected={turn ? draft.zone : undefined}
        onSelect={s.chooseZone}
        kick={showResult ? lastKick.kick : null}
        hint={turn && stage === 'zone' ? (role === 'shooter' ? 'shoot' : 'keep') : null}
        keeperColor={keeperColor}
        ballColors={ballColors}
      />
      <section className="match__panel">
        {showResult && (
          <div className={`result ${goodForYou ? 'result--good' : 'result--bad'}`} role="status">
            <span className="result__title">{OUTCOME_TITLE[lastKick.kick.outcome]}</span>
            {lastKick.kick.shooter === match.you && lastKick.kick.quality === 'PERFECT' && (
              <span className="result__note">{QUALITY_LABEL.PERFECT}</span>
            )}
            {(youShot ? lastKick.kick.autoShot : lastKick.kick.autoKeeper) && (
              <span className="result__note">Время вышло — ход сделан за вас</span>
            )}
          </div>
        )}
        {turn && (
          <>
            <TurnTimer deadline={turn.deadline} offset={s.clockOffset} />
            {prompt && <p className="match__prompt">{prompt}</p>}
            {role === 'shooter' && stage === 'power' && <Meter kind="power" onStop={s.setPower} />}
            {role === 'shooter' && stage === 'accuracy' && <Meter kind="accuracy" onStop={s.setAccuracy} />}
          </>
        )}
      </section>
    </main>
  );
}
